import React from 'react';
import PropTypes from 'prop-types';
import {result, debounce} from 'lodash';
import {
    DEFAULT_MEASURE_AGGREGATION,
} from '../../../../../models/pipeline/steps/dataset/aggregations';
import DatasourceModel from '../../../../../models/datasource';
import {FEATURE_FLAGS} from '../../../../../services/feature_flags';
import {
    columnLeaveEvent,
    columnHoverEvent,
    inputFocusEvent,
} from '../../../../../services/query_builder_events';
import {queryBuilderState} from '../../../../../services/query_builder_state';
import DataTable from '../../../../common/data_table';
import SchemaBrowser from './schema_browser';
import AttributeMenu from '../attribute_menu';

export default class ColumnSelector extends AttributeMenu {
    columnRefs = {};
    previewContainer = null;
    schemaBrowser = null;
    schemaBrowserContainer = null;

    constructor(props) {
        super(props);
        Object.assign(this.state, this.getUpdatedSelections(), {scrollTable: true});

        this.toggleDataTable = this.toggleDataTable.bind(this);
        this.registerColumnRef = this.registerColumnRef.bind(this);

        this.scrollToColumn = debounce(this.scrollToColumn.bind(this), 30);
    }

    componentDidMount() {
        super.componentDidMount();
        this.unbindColumnHoverListener = columnHoverEvent.listen((column, fromTable) => {
            this.setState({hoveredColumn: column, scrollTable: !fromTable});
        });
        this.unbindColumnLeaveListener = columnLeaveEvent.listen(() => {
            this.setState({hoveredColumn: null});
        });
        this.unbindInputFocusListener = inputFocusEvent.listen(() => {
            this.setState(this.getUpdatedSelections());
        });
        this.scrollToColumn();
    }

    componentDidUpdate(...args) {
        super.componentDidUpdate(...args);
        const [prevProps, prevState] = args;
        const {datasource, type} = this.props;
        const {searchQuery, hoveredColumn, selectedTable} = this.state;
        if (prevState.searchQuery !== searchQuery || prevProps.type !== type || prevProps.datasource !== datasource) {
            this.setState(this.getUpdatedSelections(), () => { this.scrollToColumn(true); });
        } else if (prevState.hoveredColumn !== hoveredColumn) {
            this.scrollToColumn(prevState.selectedTable !== selectedTable);
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this.unbindColumnHoverListener();
        this.unbindColumnLeaveListener();
        this.unbindInputFocusListener();
    }

    toggleDataTable(table, show = true) {
        const selectedTable = table === this.state.selectedTable || !show ? null : table;
        this.setState({selectedTable, hoveredColumn: selectedTable ? selectedTable.columns[0] : null});
    }

    registerColumnRef(column, el) {
        if (this.columnRefs) {
            this.columnRefs[column.id] = el;
        }
    }

    getUpdatedSelections() {
        const {searchQuery} = this.state;
        const {datasource} = this.props;
        const nextState = {
            selectedTable: null,
            hoveredColumn: null,
            selectedColumn: null,
            selectedAggregation: null,
            filteredDatasource: datasource,
            hoveredColumnPosition: [0, 0],
        };
        if (
            queryBuilderState.attribute.column &&
            (queryBuilderState.attribute.label === searchQuery || result(queryBuilderState.column, 'label') === searchQuery)
        ) {
            nextState.hoveredColumn = queryBuilderState.attribute.column;
            nextState.selectedTable = queryBuilderState.attribute.column.table;
            nextState.selectedAggregation = DEFAULT_MEASURE_AGGREGATION[queryBuilderState.attribute.column.type];
        } else if (searchQuery) {
            nextState.filteredDatasource = datasource.filter(searchQuery);
            nextState.hoveredColumn = result(nextState.filteredDatasource.getFirstSchema(), 'tables[0].columns[0]', null);
        }
        if (nextState.selectedTable) {
            nextState.selectedTable = nextState.filteredDatasource.getTable(nextState.selectedTable.fullName) || null;
        }
        return nextState;
    }

    updateSelectedItem(delta) {
        let {hoveredColumnPosition: [tableIndex, columnIndex]} = this.state;
        const {tables} = this.state.filteredDatasource.getFirstSchema();
        columnIndex += delta;
        if (columnIndex < 0) {
            tableIndex = tableIndex === 0 ? tables.length - 1 : tableIndex - 1;
            columnIndex = tables[tableIndex].columns.length - 1;
        }

        if (columnIndex >= tables[tableIndex].columns.length) {
            tableIndex += 1;
            columnIndex = 0;
        }
        if (tableIndex >= tables.length) {
            tableIndex = 0;
            columnIndex = 0;
        }
        const hoveredColumn = tables[tableIndex].columns[columnIndex];
        this.setState({hoveredColumnPosition: [tableIndex, columnIndex], hoveredColumn}, () => { this.scrollToColumn(); });
    }

    scrollToColumn(scrollList = false) {
        const {hoveredColumn, scrollTable} = this.state;
        if (hoveredColumn) {
            if (FEATURE_FLAGS.SHOW_PREVIEW_TABLE && scrollTable) {
                // scroll table
                const columnEl = this.columnRefs[hoveredColumn.id];

                if (columnEl) { // seems to be null in some weird cases - maybe when closing a menu too fast?
                    const tableEl = columnEl.closest('.preview-table-container');
                    const tableOffset = tableEl.offsetLeft;
                    const columnOffset = columnEl.offsetLeft + (columnEl.offsetWidth / 2);
                    const containerOffset = this.previewContainer.offsetWidth / 2;
                    this.previewContainer.scrollTo({
                        left: tableOffset + columnOffset - containerOffset,
                        behavior: 'smooth',
                    });
                }
            }

            if (scrollList && this.schemaBrowser) {
                // scroll schema browser
                const columnEl = this.schemaBrowser.tableRefs[hoveredColumn.table.id].columnRefs[hoveredColumn.id].column;
                const columnOffset = columnEl.offsetTop + (columnEl.offsetHeight / 2);
                const containerOffset = this.schemaBrowserContainer.offsetHeight / 2;
                this.schemaBrowserContainer.scrollTo({
                    top: columnOffset - containerOffset,
                });
            }
        }
    }

    shouldConsumeArrowKeys() {
        return this.state.filteredDatasource !== this.props.datasource;
    }

    renderMenu() {
        const searchQuery = queryBuilderState.searchQuery;
        const {datasource} = this.props;
        const {
            filteredDatasource,
            hoveredColumn,
            selectedTable,
            selectedColumn,
        } = this.state;
        const datasourceIsFiltered = filteredDatasource !== datasource;
        let tablesToShow = [];
        if (searchQuery && datasourceIsFiltered) {
            tablesToShow = filteredDatasource.getFirstSchema().tables;
        } else if (selectedTable) {
            tablesToShow.push(selectedTable);
        } else if (hoveredColumn) {
            tablesToShow.push(hoveredColumn.table);
        }
        return (
            <>
                <div className="databar" ref={(el) => { this.schemaBrowserContainer = el; }}>
                    <SchemaBrowser
                        datasource={filteredDatasource}
                        toggleDataTable={this.toggleDataTable}
                        expandAll={datasourceIsFiltered}
                        selectedTable={selectedTable}
                        selectedColumn={selectedColumn}
                        hoveredColumn={hoveredColumn}
                        ref={(el) => { this.schemaBrowser = el; }}
                    />
                </div>
                {FEATURE_FLAGS.SHOW_PREVIEW_TABLE && tablesToShow.length > 0 && (
                    <div
                        className={`preview-container ${tablesToShow.length ? '' : 'empty'}`}
                        ref={(el) => { this.previewContainer = el; }}
                    >
                        <div className="preview-container-tables">
                            {tablesToShow.map(table => (
                                <div className="preview-table-container" key={table.fullName}>
                                    <DataTable
                                        data={table.getSampleData()}
                                        highlightColumn={result(selectedColumn || hoveredColumn, 'id')}
                                        limit={8}
                                        fireEvents
                                        registerColumnRef={this.registerColumnRef}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </>
        )
    }
}

ColumnSelector.propTypes = {
    datasource: PropTypes.instanceOf(DatasourceModel).isRequired,
    withAggregations: PropTypes.bool,
};

ColumnSelector.defaultProps = {
    withAggregations: false,
};
